import React, { useContext, useState } from 'react';
import { Box, Button, Toolbar as MuiToolbar, SvgIcon, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../constants';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import { AddMenuButton, PinAction } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../reducers/user.selectors';
import getMuiIcon, { IconName } from '../../../utils/getMuiIcon';
import Breadcrumbs from '../../Breadcrumbs';
import IsReadOnlyChip from './IsReadOnlyChip';
import useScreenSize from '../../../utils/useScreenSize';
import { selectProduct } from '../../../reducers/products.selectors';
import AIAssistantToggler from '../../AIAssistant/Dialog/AIAssistantToggler';
import useShouldShowAIAssistant from '../../../utils/useShouldShowAIAssistant';
import { ReaderContext } from '../../Reader/ReaderContext';
import ShareDialog from '../../lernsets/ShareDialog';
import services from '../../../services';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { ampliTrackVideoUnlockedEvent } from '../../../services/track.service';
import getSearchPageUrl from '../../../utils/getSearchPageUrl';

interface ToolbarProps {
  mindItem?: MindItem
  downloadMindItem?: () => void
  pinActionCallback: (action: PinAction) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ mindItem, downloadMindItem, pinActionCallback }) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [isPublicLinkDialogOpen, setIsPublicLinkDialogOpen] = useState(false);
  const [publicLink, setPublicLink] = useState('');
  const { showNewReader } = useContext(ReaderContext);
  const { isXsScreen, isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const showDownloadButton =
    mindItem?.fileType === 'document'
    && !mindItem.isReadOnly
    && !user?.hideDownloadAction;
  const shouldShowAIAssistant = useShouldShowAIAssistant(); 
  const shouldShowRectangularAiButton = isDesktopScreen;
  const showPublicShare = user?.hasDocumentLevelAccess && mindItem?.fileType === 'video'
  && mindItem?.isShareable;

  const searchPageUrl = getSearchPageUrl(user);
  const product = useSelector(state => mindItem && selectProduct(state, mindItem.product));
  const hasProductOverview = product && !!product?.active;
  const shouldShowProductBreadcrumb = hasProductOverview && !(showNewReader && mindItem?.fileType === 'video');

  const isNewVideoReaderLayout = showNewReader && mindItem?.fileType === 'video';

  const getToolbarLeftSide = () => {
    const links = [
      {
        title: t('Search'),
        url: searchPageUrl,
        iconName: 'search' as IconName,
      },  
      {
        title: mindItem?.title,
        iconName: 'docText' as IconName,
      },
    ];

    const productPageLink = {
      title: product?.title || t('Product') || '',
      url: `/${routes.productPage.root}/${mindItem?.product}`,
      iconName: 'book' as IconName,
    };

    if (shouldShowProductBreadcrumb) {
      links.splice(1, 0, productPageLink);
    }

    return <Box data-testid='toolbar__left-side'><Breadcrumbs links={links} /></Box>;
  };

  const getDownloadButton = () => {
    if (!isDesktopScreen) {
      return (
        <IconButton
          data-testid='toolbar-download-button'
          onClick={() => downloadMindItem?.()}
          color='primary'
          size='medium'
          type='button'
          aria-label={t('Download')}
        >
          <SvgIcon component={getMuiIcon('download')} aria-hidden='true' />
        </IconButton>
      );
    }
    return (
      <Button
        variant='contained'
        size='large'
        color='primary'
        data-testid='toolbar-download-button'
        onClick={() => downloadMindItem?.()}
        type='button'
        aria-label={t('Download')}
        sx={{
          padding: `${spaces.xs} ${spaces.lg}`,
          whiteSpace: 'nowrap',
        }}
        startIcon={
          <SvgIcon
            component={getMuiIcon('download')}
            sx={{
              color: palette.primary.contrastText,
            }}
            aria-hidden='true'
          />
        }
      >
        {t('Download')}
      </Button>
    );
  };

  const onOpenShareDialog = async () => {
    if (mindItem?.id) {
      ampliTrackVideoUnlockedEvent(mindItem);
      await services.mindItem.createPublicMindItemLink(mindItem.id)
        .then(({ publicLink }) => setPublicLink(publicLink))
        .then(() => setIsPublicLinkDialogOpen(true))
        .catch((error) => {
          setIsPublicLinkDialogOpen(false);
          notifyBugsnagHandledError(error as Error);
        });
    }
  };
  const handleSharing = () => {};  // TODO: implement sharing when we have Ampli event for it
  const renderShareDialog = ({ isOpen }: { isOpen: boolean}) => {
    return (
      <ShareDialog
        title={t('Share this video with your students')}
        subTitle={t('This link is available for {{days}} days', { days: 14 })}
        linkUrl={publicLink}
        qrImageName={mindItem?.title || ''}
        onClose={() => setIsPublicLinkDialogOpen(false)} 
        open={isOpen}
        onQrDownload={handleSharing}
        onLinkCopy={handleSharing}
      />
    );
  };

  const iconButtonSx = {
    position: 'relative',
    top: 'unset',
    right: 0,
  };

  return (
    <MuiToolbar
      data-testid='toolbar'
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${palette.elevation.outlined}`,
        backgroundColor: palette.background.paperElevation2,
        padding: `${spaces.sm} ${spaces.md} !important`,
        height: customSizes.readerToolbarHeight,
      }}
    >
      {getToolbarLeftSide()}
      <Box
        data-testid='toolbar__right-side'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spaces.xs,
        }}
      >
        {
          isDesktopScreen &&
          mindItem && 
              <Box>
                <AddMenuButton
                  isPinned={!!mindItem?.isPinned}
                  id={mindItem?.id}
                  itemData={mindItem}
                  type={mindItem?.fileType}
                  buttonSx={iconButtonSx}
                  size='medium'
                  pinActionCallback={pinActionCallback}
                />
              </Box>
        }
        {
          (!isNewVideoReaderLayout) && (
            shouldShowAIAssistant && (
              <AIAssistantToggler buttonType={shouldShowRectangularAiButton ? 'rectangular' : 'round'} />
            )
          )
        }
        {
          (isNewVideoReaderLayout) && (
            showPublicShare && (
              <Button
                variant='contained'
                color='primary'
                size='large'
                type='button'
                aria-label={isXsScreen ? t('Share') : t('Unlock for students')}
                startIcon={
                  <SvgIcon 
                    component={getMuiIcon('shareVideo')}
                    aria-hidden='true'
                  />
                }
                onClick={onOpenShareDialog}
              >
                {isXsScreen ? t('Share') : t('Unlock for students')}
              </Button>
            )
          )
        }
        {
          showDownloadButton && getDownloadButton()
        }
        {
          mindItem?.fileType === 'document' && mindItem?.isReadOnly && <IsReadOnlyChip />
        }
      </Box>
      {renderShareDialog({ isOpen: isPublicLinkDialogOpen })}
    </MuiToolbar>
  );
};

export default Toolbar;
